import { makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions, useUserPermissions } from '@paintscout/ui-business'
import AddBoxIcon from '@material-ui/icons/AddBox'
import ArchiveIcon from '@material-ui/icons/Archive'
import DeleteIcon from '@material-ui/icons/Delete'
import ListIcon from '@material-ui/icons/List'
import ConvertIcon from '@material-ui/icons/SwapHoriz'
import get from 'lodash/get'
import React, { useMemo } from 'react'
import type { QuoteItemView, RenderableItem } from '@paintscout/util/builder'
import { getFeatures, getItems, getObjectLabels, getQuoteOptions } from '@paintscout/util/builder'
import { canSaveItemToSettings, saveToSettingsAction } from '../util/saveToSettingsAction'
import { useQuote } from '../context'
import AdditionalWorkSignature from '../Quote/views/QuoteView/AdditionalTable/AdditionalWorkSignature'
import ItemRowContent from './ItemRowContent'
import OutlinedItemTable from './OutlinedItemTable'
import type { OutlinedItemTableProps } from './OutlinedItemTable'

export interface AdditionalTableNextProps {
  selectedItems?: OutlinedItemTableProps['selectedItems']
  onItemSelection?: OutlinedItemTableProps['onItemSelection']
  onItemAction: (action: string, section: 'additional', item: RenderableItem) => void
  collapsed?: boolean
}

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      marginTop: theme.spacing(3)
    },
    heading: {
      marginBottom: theme.spacing(1.5)
    }
  }),
  { name: 'AdditionalTableNext' }
)

/**
 * The "Additional Work" section for the new (estimateNext) pipeline, ported from `AdditionalTable`
 * onto the shared outlined primitives. The heading (+ signature) sits above the card.
 */
export default function AdditionalTableNext({
  collapsed,
  selectedItems,
  onItemSelection,
  onItemAction
}: AdditionalTableNextProps) {
  const classes = useStyles()
  const { quote, tableView, isEditable, onReorder, onAddItem } = useQuote()
  const { options } = useClientOptions()
  const { permissionToWriteAllDocuments, permissionToEditAccountSettings } = useUserPermissions()
  const features = getFeatures({ options })
  const canConvertToLineItem = get(features, 'quotes.convertToLineItem')
  const objectLabels = getObjectLabels({ options, invoice: quote.is_invoice })

  const { addIndex, showPrices, hideAllPrices } = getQuoteOptions({ quote, options })
  const approvedView = (quote.additionalWork?.approvedView as QuoteItemView) ?? tableView

  const items: RenderableItem[] = useMemo(() => {
    return getItems({
      quote,
      section: 'additional',
      view: approvedView,
      consumer: 'customer',
      options,
      overrideOptions: { showPrices: true }
    }).map((item) => {
      const itemDescription = item.description.useCustom ? item.description.custom : item.description.default
      if (!itemDescription && !item.value && item.subItems && item.subItems.length === 1) {
        const firstSubItem = item.subItems.shift()
        return {
          ...item,
          description: { useCustom: false, default: firstSubItem.name, custom: '' },
          value: firstSubItem.value
        }
      }
      return item
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote, tableView])

  if (items.length === 0) {
    return null
  }
  if (collapsed && !isEditable) {
    return null
  }

  function handleReorder({ items, addIndex }) {
    onReorder?.({ items, section: 'additional', view: approvedView, addIndex })
  }

  return (
    <div className={classes.root}>
      <OutlinedItemTable
        sectionTitle={objectLabels.additional.plural}
        sectionAction={<AdditionalWorkSignature />}
        collapsed={collapsed}
        headerVariant="light"
        // One bordered card, like the Item table — no outline nested inside an outline (ENG-2057).
        singleBorder
        title={'Item'}
        items={items}
        selectedItems={selectedItems}
        checkboxes={isEditable}
        showAddButton={false}
        addIndex={addIndex.additional}
        section={'additional'}
        onReorder={handleReorder}
        onItemSelection={onItemSelection}
        onItemClick={
          !quote.is_invoice || permissionToWriteAllDocuments
            ? (_ev, item) => onItemAction('edit', 'additional', item as RenderableItem)
            : undefined
        }
        onAddClick={(_ev, insertBeforeKey, type) => onAddItem('additional', insertBeforeKey, type)}
        renderRowColumns={(item: RenderableItem) => {
          const actions = []
          if (!quote.is_invoice) {
            actions.push({ key: 'move-to-bid', label: `Move to ${objectLabels.quote.value}`, icon: AddBoxIcon })
          }
          actions.push({ key: 'make-option', label: 'Make Option', icon: AddBoxIcon })
          actions.push({ key: 'archive', label: 'Archive', icon: ArchiveIcon })
          if (item.type === 'group') {
            actions.push({ key: 'ungroup', label: 'Ungroup', icon: ListIcon })
          }
          if (canConvertToLineItem && item.type !== 'line_item') {
            actions.push({ key: 'convert', label: 'Convert to Line Item', icon: ConvertIcon })
          }

          if (canSaveItemToSettings({ permissionToEditAccountSettings, item })) {
            actions.push(saveToSettingsAction)
          }

          // Last, as the one destructive action — the prototype puts Delete at the foot of every
          // item menu (ENG-1922). The "Are you sure?" prompt comes from the context:
          // `handleItemAction('delete')` opens a ConfirmationDialog before removing anything.
          actions.push({ key: 'delete', label: 'Delete', icon: DeleteIcon })

          return (
            <ItemRowContent
              item={item}
              section={'additional'}
              isEditable={isEditable}
              showAmount={showPrices && !hideAllPrices}
              actions={isEditable ? actions : undefined}
              onAction={(action, it) => onItemAction(action, 'additional', it)}
            />
          )
        }}
      />
    </div>
  )
}
